import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';

export default function TransitMaster({ embedded = false }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [search, setSearch] = useState('');
  const [filterMeans, setFilterMeans] = useState('');
  const [filterLine, setFilterLine] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [seedModal, setSeedModal] = useState(false);
  const [seedFile, setSeedFile] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/employees/transit');
      if (res.ok) {
        const data = await res.json();
        setRecords(data.transit || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openEdit(rec) {
    setEditRow(rec ? {
      id: rec.id,
      metropolis: rec.metropolis || '',
      means: rec.means || '',
      line: rec.line || '',
      zone: rec.zone || '',
      locality: rec.locality || '',
      duration: rec.duration || '',
      status: rec.status || 'Active',
    } : {
      metropolis: '', means: '', line: '', zone: '', locality: '', duration: '', status: 'Active',
    });
    setEditModal(true);
  }

  async function saveRecord() {
    if (!editRow.metropolis.trim() || !editRow.means.trim() || !editRow.locality.trim()) return;
    try {
      await apiFetch('/api/employees/transit', {
        method: 'POST',
        body: JSON.stringify(editRow),
      });
      setEditModal(false);
      fetchData();
    } catch (err) { /* handled */ }
  }

  async function deleteRecord(id) {
    if (!confirm('Delete this transit record?')) return;
    try {
      await apiFetch(`/api/employees/transit/${id}`, { method: 'DELETE' });
      fetchData();
    } catch (err) { /* handled */ }
  }

  // Seed from uploaded Excel file
  async function handleSeed() {
    if (!seedFile) return;
    setSeeding(true);
    setSeedMsg('');
    try {
      const XLSX = await import('xlsx');
      const buf = await seedFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      // Normalize keys (trim spaces)
      const normalized = rows.map(r => {
        const out = {};
        Object.entries(r).forEach(([k, v]) => { out[k.trim()] = v; });
        return out;
      });

      const seedRecords = normalized.map(r => ({
        metropolis: r.Metropolis || r.metropolis || '',
        means: r.Means || r.means || '',
        line: r.Line || r.line || '',
        zone: r.Zone || r.zone || '',
        locality: r.Locality || r.locality || '',
        duration: r.Duration || r.duration || '',
      })).filter(r => r.metropolis && r.locality);

      const res = await apiFetch('/api/employees/transit/seed', {
        method: 'POST',
        body: JSON.stringify({ records: seedRecords }),
      });
      if (res.ok) {
        const d = await res.json();
        setSeedMsg(`Successfully imported ${d.inserted} records.`);
        fetchData();
      } else {
        const d = await res.json().catch(() => ({}));
        setSeedMsg(d.error || 'Import failed');
      }
    } catch (err) {
      setSeedMsg(`Error: ${err.message}`);
    }
    setSeeding(false);
  }

  // Derive unique filter options from data
  const uniqueMeans = [...new Set(records.map(r => r.means).filter(Boolean))].sort();
  const uniqueLines = [...new Set(records.map(r => r.line).filter(Boolean))].sort();
  const uniqueZones = [...new Set(records.map(r => r.zone).filter(Boolean))].sort();

  const meansOptions = [{ value: '', label: 'All Means' }, ...uniqueMeans.map(m => ({ value: m, label: m }))];
  const lineOptions = [{ value: '', label: 'All Lines' }, ...uniqueLines.map(l => ({ value: l, label: l }))];
  const zoneOptions = [{ value: '', label: 'All Zones' }, ...uniqueZones.map(z => ({ value: z, label: z }))];

  const filteredRecords = records.filter(r => {
    if (filterMeans && r.means !== filterMeans) return false;
    if (filterLine && r.line !== filterLine) return false;
    if (filterZone && r.zone !== filterZone) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.locality || '').toLowerCase().includes(s) ||
           (r.metropolis || '').toLowerCase().includes(s) ||
           (r.zone || '').toLowerCase().includes(s) ||
           (r.line || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {!embedded && <h2 style={{ margin: 0 }}>Transit Master</h2>}
        <div style={{ display: 'flex', gap: 8, marginLeft: embedded ? 0 : 'auto' }}>
          <button className="btn-neon" onClick={() => setSeedModal(true)} style={{ '--page-neon': '#FFB300' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import Excel
          </button>
          <button className="btn-neon" onClick={() => openEdit(null)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Record
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search localities..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', width: 220 }} />
        <select value={filterMeans} onChange={e => setFilterMeans(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: '#e0e0e0' }}>
          {meansOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filterLine} onChange={e => setFilterLine(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: '#e0e0e0' }}>
          {lineOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filterZone} onChange={e => setFilterZone(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: '#e0e0e0' }}>
          {zoneOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span style={{ padding: '8px 0', color: '#666', fontSize: 13 }}>
          {filteredRecords.length} of {records.length} records
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Metropolis</th>
              <th>Means</th>
              <th>Line</th>
              <th>Zone</th>
              <th>Locality</th>
              <th>Duration</th>
              <th>Status</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                {search || filterMeans || filterLine || filterZone ? 'No matching records found' : 'No transit data. Click "Import Excel" to seed from a file or "New Record" to add manually.'}
              </td></tr>
            ) : filteredRecords.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight: 500 }}>{r.metropolis}</td>
                <td>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 4,
                    background: r.means === 'Local Train' ? 'rgba(0,224,255,0.12)' :
                                r.means === 'Bus' ? 'rgba(57,255,20,0.12)' : 'rgba(255,107,0,0.12)',
                    color: r.means === 'Local Train' ? '#00e0ff' : r.means === 'Bus' ? '#39FF14' : '#FF6B00',
                  }}>{r.means}</span>
                </td>
                <td style={{ color: '#aaa' }}>{r.line || '—'}</td>
                <td style={{ color: '#FFB300' }}>{r.zone || '—'}</td>
                <td style={{ fontWeight: 500 }}>{r.locality}</td>
                <td style={{ color: '#888' }}>{r.duration || '—'}</td>
                <td>
                  <span className={`badge ${r.status === 'Active' ? 'badge-green' : 'badge-red'}`}>
                    {r.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-neon btn-sm" onClick={() => openEdit(r)} title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="btn-neon btn-sm" onClick={() => deleteRecord(r.id)} title="Delete" style={{ '--page-neon': '#ff4444' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Edit / Create Modal */}
      <Modal title={editRow?.id ? 'Edit Transit Record' : 'New Transit Record'} isOpen={editModal} onClose={() => setEditModal(false)} size="md">
        {editRow && <>
          <div className="form-row">
            <FormField label="Metropolis" name="metropolis" required
              value={editRow.metropolis} placeholder="e.g. MMR"
              onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
            <FormField label="Means" name="means" type="select" required
              value={editRow.means}
              onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
              options={[
                { value: 'Local Train', label: 'Local Train' },
                { value: 'Bus', label: 'Bus' },
                { value: 'Taxi', label: 'Taxi' },
                { value: 'Metro', label: 'Metro' },
                { value: 'Auto', label: 'Auto' },
                { value: 'Other', label: 'Other' },
              ]}
              placeholder="Select Means" />
          </div>
          <div className="form-row">
            <FormField label="Line" name="line"
              value={editRow.line} placeholder="e.g. Western Line"
              onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
            <FormField label="Zone" name="zone"
              value={editRow.zone} placeholder="e.g. South Mumbai"
              onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          </div>
          <div className="form-row">
            <FormField label="Locality" name="locality" required
              value={editRow.locality} placeholder="e.g. Churchgate"
              onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
            <FormField label="Duration" name="duration"
              value={editRow.duration} placeholder="e.g. 45 mins"
              onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          </div>
          <FormField label="Status" name="status" type="select"
            value={editRow.status}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveRecord}>Save</button>
          </div>
        </>}
      </Modal>

      {/* Import / Seed Modal */}
      <Modal title="Import Transit Data from Excel" isOpen={seedModal} onClose={() => { setSeedModal(false); setSeedMsg(''); setSeedFile(null); }} size="md">
        <div>
          <p style={{ color: '#aaa', fontSize: 13, marginBottom: 12 }}>
            Upload an Excel file with columns: <strong style={{ color: '#e0e0e0' }}>Metropolis, Means, Line, Zone, Locality, Duration</strong>.
            All rows will be imported as new records.
          </p>
          <input type="file" accept=".xlsx,.xls,.csv"
            onChange={e => { setSeedFile(e.target.files[0]); setSeedMsg(''); }}
            style={{ marginBottom: 12 }} />
          {seedFile && (
            <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
              Selected: {seedFile.name} ({Math.round(seedFile.size / 1024)} KB)
            </div>
          )}
          {seedMsg && (
            <div style={{
              padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13,
              background: seedMsg.startsWith('Error') || seedMsg.includes('failed') ? 'rgba(255,68,68,0.15)' : 'rgba(57,255,20,0.12)',
              color: seedMsg.startsWith('Error') || seedMsg.includes('failed') ? '#ff4444' : '#39FF14',
            }}>{seedMsg}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => { setSeedModal(false); setSeedMsg(''); setSeedFile(null); }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSeed} disabled={!seedFile || seeding}>
              {seeding ? 'Importing...' : 'Import'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
